import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw } from "lucide-react";

/**
 * Retainer Health — Daily. The team's "Daily Numbers" tracker, computed live
 * from production_assets. The funnel is bound to the real ASSET_PIPELINES
 * stages (Concept → Script → Treatment → Shooting → Edit → Client Review →
 * Approved → Live) — no synthetic stages. Each row counts assets at or past its
 * stage; status pills come from pace (working-days elapsed) vs cumulative.
 * Owner-gated (REVIEWS_ALLOWED_EMAILS) for the pilot. Served by
 * GET /production/retainer-health.
 */

const RAG = {
  green: { bg: "#dcfce7", fg: "#15803d", label: "On track" },
  amber: { bg: "#fef3c7", fg: "#b45309", label: "At risk" },
  red: { bg: "#fee2e2", fg: "#b91c1c", label: "Behind" },
};

function Pill({ state, text }) {
  const r = RAG[state] || RAG.green;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 6, background: r.bg, color: r.fg, whiteSpace: "nowrap" }}>
      {text || r.label}
    </span>
  );
}

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function RetainerHealth() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/production/retainer-health?month=${month}`);
      setData(res.data);
      const brands = res.data?.brands || [];
      setActive((cur) => (brands.find((b) => b.brand === cur) ? cur : brands[0]?.brand || null));
      setFilter(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load retainer health");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const brands = data?.brands || [];
  const cur = useMemo(() => brands.find((b) => b.brand === active) || null, [brands, active]);

  const FCAT = {
    "Amber items": (a) => a.risk === "amber",
    "Red items": (a) => a.risk === "red",
    "Missed go-lives": (a) => a.go_live && a.go_live < (data?.today || "") && a.stage !== "Live",
    "Client-blocked": (a) => a.stage === "Client Review" || a.stage === "Revisions",
  };
  const rows = useMemo(() => {
    if (!cur) return [];
    if (filter && FCAT[filter]) return cur.assets.filter(FCAT[filter]);
    return cur.assets.filter((a) => a.risk !== "green");
  }, [cur, filter]);

  const wrap = { maxWidth: 1180, margin: "0 auto", padding: "0 20px" };
  const th = { textAlign: "right", fontWeight: 600, fontSize: 12, color: "#6b7280", padding: "10px 8px" };
  const td = { textAlign: "right", fontSize: 13, padding: "9px 8px", borderTop: "1px solid #f0f0f0" };

  return (
    <div style={{ minHeight: "100vh", background: "#fafaf9", paddingBottom: 60 }}>
      <div style={{ ...wrap, paddingTop: 24 }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#6b7280", background: "none", border: "none", cursor: "pointer", marginBottom: 14 }}>
          <ArrowLeft size={15} /> Back
        </button>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: "#111" }}>Retainer health — daily</h1>
            <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>
              Computed from the production pipeline{data?.generated_at ? ` · updated ${new Date(data.generated_at).toLocaleString()}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="month" value={month} max={thisMonth()} onChange={(e) => setMonth(e.target.value)}
              style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }} />
            <button onClick={load} title="Refresh" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 12px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
        ) : !brands.length ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: "#9ca3af" }}>
            No production assets in scope for {month}. (Assets count toward a month by their go-live date.)
          </div>
        ) : (
          <>
            {/* Portfolio roll-up */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, margin: "16px 0 26px" }}>
              {brands.map((b) => {
                const on = b.brand === active;
                return (
                  <div key={b.brand} onClick={() => { setActive(b.brand); setFilter(null); }}
                    style={{ cursor: "pointer", padding: "12px 14px", borderRadius: 12, background: "#fff", border: on ? "2px solid #2563eb" : "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{b.brand}</span>
                      <Pill state={b.overall} />
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{b.planned} assets · {b.pace}% delivered</div>
                  </div>
                );
              })}
            </div>

            {cur && (
              <>
                {/* Funnel */}
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 2 }}>Daily numbers · {cur.brand}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>Each row counts assets at or past its production stage</div>
                <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 12, background: "#fff" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 580 }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: "left", paddingLeft: 14 }}>Stage</th>
                        <th style={th}>Planned</th><th style={th}>Target</th><th style={th}>Today</th>
                        <th style={th}>Cumulative</th><th style={th}>Balance</th>
                        <th style={{ ...th, textAlign: "left", paddingLeft: 12 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cur.funnel.map((f) => (
                        <tr key={f.stage}>
                          <td style={{ ...td, textAlign: "left", paddingLeft: 14, fontWeight: f.stage === "Live" ? 600 : 400, color: "#111" }}>{f.stage}</td>
                          <td style={{ ...td, color: "#9ca3af" }}>{f.planned}</td>
                          <td style={{ ...td, color: "#6b7280" }}>{f.target}</td>
                          <td style={{ ...td, color: "#6b7280" }}>+{f.today}</td>
                          <td style={{ ...td, fontWeight: 600, color: "#111" }}>{f.cumulative}</td>
                          <td style={{ ...td, color: f.balance > 0 ? "#6b7280" : "#cbd5e1" }}>{f.balance}</td>
                          <td style={{ ...td, textAlign: "left", paddingLeft: 12 }}><Pill state={f.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Forward planning */}
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", margin: "24px 0 8px" }}>Forward planning</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10 }}>
                  {[
                    ["Amber items", cur.forward.amber, "amber", true],
                    ["Red items", cur.forward.red, "red", true],
                    ["Missed go-lives", cur.forward.missed, "red", true],
                    ["Client-blocked", cur.forward.client_blocked, "amber", true],
                    ["Tomorrow planned", cur.forward.tomorrow_planned, null, false],
                    ["Tomorrow ready", cur.forward.tomorrow_ready, null, false],
                    ["Tomorrow not ready", cur.forward.tomorrow_not_ready, null, false],
                  ].map(([label, val, tone, clickable]) => {
                    const col = val > 0 && tone ? RAG[tone].fg : "#111";
                    const on = filter === label;
                    return (
                      <div key={label} onClick={clickable ? () => setFilter(on ? null : label) : undefined}
                        style={{ padding: "12px 14px", borderRadius: 8, background: "#fff", border: "1px solid #eee", cursor: clickable ? "pointer" : "default", outline: on ? "2px solid #2563eb" : "none" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: val > 0 ? col : "#cbd5e1" }}>{val}</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Asset drill-down */}
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "24px 0 8px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Assets · {filter ? filter.toLowerCase() : "at-risk & blocked"}</div>
                  {filter && <button onClick={() => setFilter(null)} style={{ fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}>Show all ✕</button>}
                </div>
                <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 12, background: "#fff" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                    <thead>
                      <tr>
                        {["Asset", "Stage", "Risk", "Go-live", "Owner", "Note"].map((h, i) => (
                          <th key={h} style={{ ...th, textAlign: "left", paddingLeft: i === 0 ? 14 : 8 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "#9ca3af" }}>Nothing in this state — clean.</td></tr>
                      ) : rows.map((a) => (
                        <tr key={a.asset_id}>
                          <td style={{ ...td, textAlign: "left", paddingLeft: 14, color: "#111" }}>
                            <span style={{ fontWeight: 500 }}>{a.title || "Untitled"}</span>
                            <span style={{ color: "#9ca3af", fontSize: 12 }}>  ·  {a.asset_type}{a.platform ? ` · ${a.platform}` : ""}</span>
                          </td>
                          <td style={{ ...td, textAlign: "left", color: "#6b7280" }}>{a.stage}</td>
                          <td style={{ ...td, textAlign: "left" }}><Pill state={a.risk} /></td>
                          <td style={{ ...td, textAlign: "left", color: "#6b7280" }}>{a.go_live || "—"}</td>
                          <td style={{ ...td, textAlign: "left", color: "#9ca3af" }}>{a.owner}</td>
                          <td style={{ ...td, textAlign: "left", color: "#6b7280" }}>{a.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
